import { OrganisationProductSummary } from "@fero/components";
import type { SelectedProduct } from "@fero/stores";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { CurrencyRenderer } from "components/widgets/currency-renderer/currency-renderer";
import { InfoTooltip } from "components/widgets/info-tooltip/info-tooltip";
import { CurrencyInput } from "components/widgets/inputs/currency-input/currency-input";
import { RoundingInput } from "components/widgets/inputs/rounding-input/rounding-input";
import { type FormikProps, FormikProvider } from "formik";
import React, { useEffect } from "react";
import { useTradeSaleHelpers } from "../hooks";
import type { SelectedProductsFormValues } from "../new-organisation-purchase-request-setup-page";

interface ColumnDef {
  id: string;
  label: React.ReactNode;
  flex: number;
  align?: "left" | "right" | "center";
  renderCell: (row: SelectedProduct) => React.ReactNode;
}

const TableHeader = ({ columns }: { columns: ColumnDef[] }) => (
  <TableHead>
    <TableRow>
      {columns.map((column) => (
        <TableCell
          key={column.id}
          align={column.align}
          sx={{ flex: column.flex }}
        >
          {column.label}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

const TableRows = ({
  selectedProducts,
  columns,
  page,
  rowsPerPage,
}: {
  selectedProducts: SelectedProduct[];
  columns: ColumnDef[];
  page: number;
  rowsPerPage: number;
}) => {
  if (selectedProducts.length === 0) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={columns.length} align="center">
            <Alert severity="info">No products selected</Alert>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody>
      {selectedProducts
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((row) => (
          <TableRow
            key={row.summary.id}
            sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
          >
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align}
                sx={{ flex: column.flex }}
              >
                {column.renderCell(row)}
              </TableCell>
            ))}
          </TableRow>
        ))}
    </TableBody>
  );
};

const FooterRow = ({
  label,
  component,
  colCount,
}: {
  label: string;
  component: JSX.Element;
  colCount: number;
}) => {
  return (
    <TableRow sx={{ backgroundColor: "common.grey1", borderTop: 0 }}>
      <TableCell colSpan={colCount - 3} />
      <TableCell colSpan={2} align="right">
        <Typography variant={"subtitle1Bold"}>{label}</Typography>
      </TableCell>
      <TableCell colSpan={1}>{component}</TableCell>
    </TableRow>
  );
};

const SalePriceCell: React.FC<{
  row: SelectedProduct;
  onChangeSalePrice: (salePrice: string) => void;
}> = ({ row, onChangeSalePrice }) => {
  const { nominatedPurchaser } = useTradeSaleHelpers();
  const [salePrice, setSalePrice] = React.useState(String(row.salePrice ?? 0));

  const onSalePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSalePrice(e.target.value);
    onChangeSalePrice(e.target.value);
  };

  return (
    <CurrencyInput
      name={`${row.productId}.salePrice`}
      currency={nominatedPurchaser.currency}
      value={salePrice}
      onChange={onSalePriceChange}
    />
  );
};

const DiscountCell: React.FC<{
  row: SelectedProduct;
  onChangeDiscount: (discount: string) => void;
}> = ({ row, onChangeDiscount }) => {
  const [discount, setDiscount] = React.useState(
    String(row.lineItemDiscount ?? 0),
  );

  const onDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDiscount(e.target.value);
    onChangeDiscount(e.target.value);
  };

  return (
    <RoundingInput
      value={discount === "0" ? "" : discount}
      precision={2}
      endAdornment="%"
      onChange={(e) => {
        const newValue = e.target.value === "" ? "0" : e.target.value;
        const clampedValue = Math.min(Number(newValue), 100).toString();
        onDiscountChange({
          target: { value: clampedValue },
        } as React.ChangeEvent<HTMLInputElement>);
      }}
    />
  );
};

const PaginatedSubtotalFooter = ({
  form,
  selectedProducts,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  columnsLength,
}: {
  form: FormikProps<SelectedProductsFormValues>;
  selectedProducts: SelectedProduct[];
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  rowsPerPage: number;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  columnsLength: number;
}) => {
  const { nominatedPurchaser } = useTradeSaleHelpers();
  const netSubtotal = selectedProducts.reduce(
    (acc, curr) => acc + curr.subtotal,
    0,
  );

  const discountAmount =
    (netSubtotal * Number(form.values.dealDiscountPercentage)) / 100;
  const totalAfterDiscount = Number.isNaN(discountAmount)
    ? netSubtotal
    : netSubtotal - discountAmount;

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <TableFooter>
      <FooterRow
        label="Discount %"
        component={
          <RoundingInput
            name="dealDiscountPercentage"
            value={
              form.values.dealDiscountPercentage === "0"
                ? ""
                : form.values.dealDiscountPercentage
            }
            precision={2}
            endAdornment="%"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const newValue = e.target.value === "" ? "0" : e.target.value;
              const clampedValue = Math.min(Number(newValue), 100).toString();
              form.setFieldValue("dealDiscountPercentage", clampedValue);
            }}
          />
        }
        colCount={columnsLength}
      />
      <FooterRow
        label="Net Subtotal"
        component={
          <CurrencyRenderer
            fontWeight="bold"
            sx={{ paddingY: 2 }}
            currency={nominatedPurchaser.currency}
            value={totalAfterDiscount}
            maximumFractionDigits={2}
          />
        }
        colCount={columnsLength}
      />
      <TableRow>
        <TableCell colSpan={columnsLength} align="right">
          <TablePagination
            colSpan={columnsLength}
            rowsPerPageOptions={[50, 100, 250, { label: "All", value: -1 }]}
            count={selectedProducts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableCell>
      </TableRow>
    </TableFooter>
  );
};

export const NewProductSelectionTable = ({
  form,
}: {
  form: FormikProps<SelectedProductsFormValues>;
}) => {
  const { nominatedPurchaser, getSalePriceLabel } = useTradeSaleHelpers();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);

  const [isDiscountColumnVisible, setIsDiscountColumnVisible] =
    React.useState(false);
  const [isFreeStockColumnVisible, setIsFreeStockColumnVisible] =
    React.useState(false);
  const showProducible = form.values.selectedProducts.some(
    (product) => product.producible !== 0,
  );
  const hasFreeUnits = form.values.selectedProducts.some(
    (product) => product.freeUnits != null && product.freeUnits > 0,
  );

  useEffect(() => {
    if (nominatedPurchaser.savedDiscountPct !== null)
      setIsDiscountColumnVisible(true);
    else setIsDiscountColumnVisible(false);
  }, [nominatedPurchaser]);

  useEffect(() => {
    if (hasFreeUnits) setIsFreeStockColumnVisible(true);
  }, [hasFreeUnits]);

  const calculateSubtotal = (
    quantity: number,
    salePrice: number,
    lineItemDiscount: number,
  ) => {
    const subtotal = quantity * salePrice;
    return subtotal * (1 - lineItemDiscount / 100);
  };

  const onQuantityChange = (productId: string, quantity: number) => {
    const product = form.values.selectedProducts.find(
      (p) => p.productId === productId,
    );
    if (product) {
      const maxQuantity = product.available + product.producible;
      const updatedProducts = form.values.selectedProducts.map((p) =>
        p.productId === productId
          ? {
              ...p,
              quantity,
              subtotal: calculateSubtotal(
                quantity,
                p.salePrice,
                product.lineItemDiscount,
              ),
            }
          : p,
      );

      form.setFieldValue("selectedProducts", updatedProducts, false);

      if (quantity > maxQuantity) {
        form.setFieldError(
          `selectedProducts.${productId}.quantity`,
          `Quantity cannot exceed ${maxQuantity}`,
        );
      } else if (quantity + product.freeUnits > maxQuantity) {
        form.setFieldError(
          `selectedProducts.${productId}.quantity`,
          `Total quantity (quantity + free stock) cannot exceed ${maxQuantity}`,
        );
      } else {
        form.setFieldError(`selectedProducts.${productId}.quantity`, undefined);
      }
    }
  };

  const onFreeStockChange = (productId: string, freeUnits: number) => {
    const product = form.values.selectedProducts.find(
      (p) => p.productId === productId,
    );
    if (product) {
      const maxQuantity = product.available + product.producible;
      const updatedProducts = form.values.selectedProducts.map((p) =>
        p.productId === productId
          ? {
              ...p,
              freeUnits,
            }
          : p,
      );

      form.setFieldValue("selectedProducts", updatedProducts, false);

      if (freeUnits > maxQuantity) {
        form.setFieldError(
          `selectedProducts.${productId}.freeStock`,
          `Free Stock cannot exceed ${maxQuantity}`,
        );
      } else if (product.quantity + freeUnits > maxQuantity) {
        form.setFieldError(
          `selectedProducts.${productId}.freeStock`,
          `Total quantity (quantity + free stock) cannot exceed ${maxQuantity}`,
        );
      } else {
        form.setFieldError(
          `selectedProducts.${productId}.freeStock`,
          undefined,
        );
      }
    }
  };

  const onSalePriceChange = (productId: string, salePrice: string) => {
    const salePriceNumber = salePrice ? Number.parseFloat(salePrice) : 0;
    const product = form.values.selectedProducts.find(
      (p) => p.productId === productId,
    );
    if (product) {
      const subtotal = calculateSubtotal(
        product.quantity,
        salePriceNumber,
        product.lineItemDiscount,
      );
      form.setFieldValue(
        "selectedProducts",
        form.values.selectedProducts.map((p) =>
          p.productId === productId
            ? { ...p, salePrice: salePriceNumber, subtotal }
            : p,
        ),
        false,
      );
    }
  };

  const onDiscountChange = (productId: string, discount: string) => {
    const discountNumber = discount ? Number.parseFloat(discount) : 0;
    const product = form.values.selectedProducts.find(
      (p) => p.productId === productId,
    );
    if (product) {
      const subtotal = calculateSubtotal(
        product.quantity,
        product.salePrice,
        discountNumber,
      );
      form.setFieldValue(
        "selectedProducts",
        form.values.selectedProducts.map((p) =>
          p.productId === productId
            ? { ...p, lineItemDiscount: discountNumber, subtotal }
            : p,
        ),
        false,
      );
    }
  };

  const onRowDelete = (productId: string) => {
    form.setFieldValue(
      "selectedProducts",
      form.values.selectedProducts.filter(
        (product) => product.productId !== productId,
      ),
    );
  };

  const resetDiscounts = () => {
    const updatedProducts = form.values.selectedProducts.map((product) => ({
      ...product,
      lineItemDiscount: 0,
      subtotal: calculateSubtotal(product.quantity, product.salePrice, 0),
    }));
    form.setFieldValue("selectedProducts", updatedProducts, false);
  };

  const resetFreeStock = () => {
    const updatedProducts = form.values.selectedProducts.map((product) => ({
      ...product,
      freeUnits: 0,
      subtotal: calculateSubtotal(
        product.quantity,
        product.salePrice,
        product.lineItemDiscount,
      ),
    }));
    form.setFieldValue("selectedProducts", updatedProducts, false);
  };

  const handleFreeStockToggle = () => {
    if (isFreeStockColumnVisible) {
      resetFreeStock(); // Reset free stock values when removing the column
    }
    setIsFreeStockColumnVisible(!isFreeStockColumnVisible);
  };

  const handleDiscountToggle = () => {
    if (isDiscountColumnVisible) {
      resetDiscounts(); // Reset discounts when removing the column
    }
    setIsDiscountColumnVisible(!isDiscountColumnVisible);
  };

  const PRODUCT_TABLE_COLUMNS: ColumnDef[] = [
    {
      id: "product",
      label: "Product",
      flex: 1,
      align: "left",
      renderCell: (row) => (
        <OrganisationProductSummary {...row?.summary?.value} />
      ),
    },
    {
      id: "available",
      label: (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
        >
          <Typography fontWeight="bold">Available</Typography>
          <InfoTooltip
            title={
              <p>
                Prices represent Net Payable Per Unit of <u>available</u>{" "}
                products. They are subject to change.
              </p>
            }
          />
        </Stack>
      ),
      flex: 1,
      align: "right",
      renderCell: (row) => {
        const isCaskProduct = row.summary.value.unitType === "cask";
        const displayText = isCaskProduct
          ? `1 cask (${row.available} LPA)`
          : `${row.available} ${row.summary.value.unitType}`;

        return <Typography>{displayText}</Typography>;
      },
    },
    ...(showProducible
      ? [
          {
            id: "producible",
            label: (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="flex-end"
              >
                <Typography fontWeight="bold">Producible</Typography>
                <InfoTooltip
                  title={
                    <p>
                      Prices represent Net Payable Per Unit of <u>unfinished</u>{" "}
                      products. They are subject to change and do not include
                      production costs.
                    </p>
                  }
                />
              </Stack>
            ),
            flex: 1,
            align: "right" as const,
            renderCell: (row: SelectedProduct) => (
              <Typography>
                {row.producible} {row.summary.value.unitType}
              </Typography>
            ),
          },
        ]
      : []),
    {
      id: "quantity",
      label: "Quantity",
      flex: 1,
      align: "right",
      renderCell: (row) => {
        const productErrors = form.errors.selectedProducts;
        let quantityError: string | undefined;

        if (productErrors && typeof productErrors !== "string") {
          // @ts-ignore
          const productError = productErrors[row.productId];
          if (
            productError &&
            typeof productError !== "string" &&
            "quantity" in productError
          ) {
            quantityError = productError.quantity as string;
          }
        }

        const isCaskProduct = row.summary.value.unitType === "cask";
        const precision = isCaskProduct ? 2 : 0;

        if (isCaskProduct) {
          return (
            <RoundingInput
              value={`1 cask (${row.quantity} LPA)`}
              disabled
              precision={2}
              endAdornment=""
              error={!!quantityError}
              helperText={quantityError}
            />
          );
        }

        return (
          <RoundingInput
            value={row.quantity === 0 ? "" : row.quantity}
            onChange={(e) => {
              onQuantityChange(row.productId, Number(e.target.value));
            }}
            onFocus={(e) => {
              if (row.quantity === 0) {
                e.target.value = "";
              }
            }}
            onBlur={(e) => {
              if (e.target.value === "") {
                onQuantityChange(row.productId, 0);
              }
            }}
            precision={precision}
            endAdornment={row.summary.value.unitType}
            error={!!quantityError}
            helperText={quantityError}
          />
        );
      },
    },
    {
      id: "salePrice",
      label: `Sale Price ${getSalePriceLabel()}`,
      flex: 1,
      align: "right",
      renderCell: (row) => (
        <SalePriceCell
          row={row}
          onChangeSalePrice={(price) => onSalePriceChange(row.productId, price)}
        />
      ),
    },
    ...(isDiscountColumnVisible
      ? [
          {
            id: "lineItemDiscount",
            label: "Line Discount",
            flex: 1,
            align: "right" as const,
            renderCell: (row: SelectedProduct) => (
              <DiscountCell
                row={row}
                onChangeDiscount={(discount) =>
                  onDiscountChange(row.productId, discount)
                }
              />
            ),
          },
        ]
      : []),
    ...(isFreeStockColumnVisible
      ? [
          {
            id: "freeStock",
            label: "Free Stock",
            flex: 1,
            align: "right" as const,
            renderCell: (row: SelectedProduct) => {
              const productErrors = form.errors.selectedProducts;
              let freeStockError: string | undefined;

              if (productErrors && typeof productErrors !== "string") {
                // @ts-ignore
                const productError = productErrors[row.productId];
                if (
                  productError &&
                  typeof productError !== "string" &&
                  "freeStock" in productError
                ) {
                  freeStockError = productError.freeStock as string;
                }
              }

              return (
                <RoundingInput
                  value={row.freeUnits === 0 ? "" : row.freeUnits}
                  onChange={(e) => {
                    onFreeStockChange(row.productId, Number(e.target.value));
                  }}
                  disabled={row.summary.value.unitType === "cask"}
                  onFocus={(e) => {
                    if (row.freeUnits === 0) {
                      e.target.value = "";
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "") {
                      onFreeStockChange(row.productId, 0);
                    }
                  }}
                  precision={0}
                  error={!!freeStockError}
                  helperText={freeStockError}
                />
              );
            },
          },
        ]
      : []),
    {
      id: "subtotal",
      label: (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
        >
          <Typography fontWeight="bold">Subtotal</Typography>
        </Stack>
      ),
      flex: 3,
      align: "right",
      renderCell: (row) => (
        <CurrencyRenderer
          currency={nominatedPurchaser.currency}
          value={row.subtotal}
          maximumFractionDigits={2}
          sx={{ minWidth: 100 }}
        />
      ),
    },
    {
      id: "actions",
      label: "",
      flex: 0.5,
      align: "right",
      renderCell: (row) => (
        <IconButton
          onClick={() => onRowDelete(row.productId)}
          aria-label="delete"
        >
          <DeleteOutlineIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <FormikProvider value={form}>
      <Stack direction="row" justifyContent="flex-end" sx={{ marginBottom: 2 }}>
        <Button
          sx={{ mr: 2 }}
          variant="outlined"
          onClick={handleFreeStockToggle}
        >
          {isFreeStockColumnVisible ? "REMOVE FREE STOCK" : "ADD FREE STOCK"}
        </Button>
        <Button variant="outlined" onClick={handleDiscountToggle}>
          {isDiscountColumnVisible ? "REMOVE DISCOUNT" : "ADD DISCOUNT"}
        </Button>
      </Stack>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="selected products table">
          <TableHeader columns={PRODUCT_TABLE_COLUMNS} />
          <TableRows
            selectedProducts={form.values.selectedProducts}
            columns={PRODUCT_TABLE_COLUMNS}
            page={page}
            rowsPerPage={rowsPerPage}
          />
          <PaginatedSubtotalFooter
            form={form}
            selectedProducts={form.values.selectedProducts}
            columnsLength={PRODUCT_TABLE_COLUMNS.length}
            page={page}
            rowsPerPage={rowsPerPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        </Table>
      </TableContainer>
    </FormikProvider>
  );
};
